import React, { useRef } from 'react'
import { useScrambleBlurIn } from '../../../hooks/useScrambleBlurIn'
import styles from './styles.module.scss'

// Shared text for the claim journey (eligibility → claim form → sign → upload),
// so every step's heading, supporting copy and small print look the same.
// Margins are defaults only: a className passed in always wins over them.

type Align = 'left' | 'center'

interface TextProps {
    children: React.ReactNode
    align?: Align
    className?: string
}

const classes = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ')

interface HeadingProps extends TextProps {
    as?: 'h1' | 'h2' | 'h3' | 'p'
    // Characters scramble and blur in on mount (key it per step to replay)
    scrambleIn?: boolean
}

// A step or page heading, e.g. "Some final details"
export const Heading: React.FC<HeadingProps> = ({
    children,
    as: Tag = 'h2',
    align = 'left',
    scrambleIn = false,
    className,
}) => {
    const ref = useRef<HTMLHeadingElement>(null)
    useScrambleBlurIn(ref, { duration: 0.38, stagger: 0.012, scramble: 0.4 }, scrambleIn)
    return (
        <Tag
            ref={ref}
            className={classes(styles.heading, styles[align], scrambleIn && styles.scrambleIn, className)}
        >
            {children}
        </Tag>
    )
}

// The quieter lead-in inside a heading, e.g. "Thanks, Lewis."
export const HeadingMuted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className={styles.headingMuted}>{children}</span>
)

// Supporting copy under a heading
export const Lead: React.FC<TextProps> = ({ children, align = 'left', className }) => (
    <p className={classes(styles.lead, styles[align], className)}>{children}</p>
)

// Consent, disclaimers and other small print
export const FinePrint: React.FC<TextProps> = ({ children, align = 'left', className }) => (
    <p className={classes(styles.finePrint, styles[align], className)}>{children}</p>
)
